/**
 * spinnerThemes.tsx — vibe-designer 1.557.0
 *
 * The registry of Pi streaming "thinking" indicator THEMES. Each theme is a
 * small sized animation component + a rotating phrase pool. `StreamingIndicator`
 * resolves the operator's chosen theme (or 'rotate') from chatPrefsStore and
 * renders it.
 *
 * Art: cosmos orbit + magic wand are inline SVG; day & night is a 3-D CSS coin;
 * puppy + rainy stroll are real image-to-video "mini-movie" loops (transparent
 * animated WebP, background-removed). Non-photo animation CSS lives in
 * ./spinnerThemes.css (every rule scoped under `.vibe-spinner`, every
 * @keyframes `vsp-`-prefixed). The component tree does NOT own `.vibe-spinner`
 * — StreamingIndicator's root carries it so the scoped CSS applies.
 *
 * The theme ids MUST equal SPINNER_VARIANTS in chatPrefsStore.ts — a test
 * (spinnerThemes.test.tsx) asserts this so the store's validation set and this
 * registry can never drift.
 *
 * NOTE (1.557.0): the puppy + girl themes are now genuine i2v loops — the puppy
 * a sit→run→jump mini-movie (WAN FLF2V pose keyframes), the girl a strict-profile
 * umbrella walk-cycle (Flux dev keyframe → WAN I2V), both keyed transparent. An
 * animated WebP keeps playing even under prefers-reduced-motion (CSS can't pause
 * it), so each ships a static first-frame POSTER PNG that the component swaps in
 * when reduced motion is requested. Default theme stays `cosmos`.
 */
/* eslint-disable react-refresh/only-export-components --
 * This is a theme REGISTRY module, not an HMR component entry point: its
 * animation components live inside the exported SPINNER_THEMES array, and it
 * intentionally co-exports data (SPINNER_THEMES / ids / phrases) and helpers
 * (resolveSpinnerTheme). Fast Refresh's one-component-per-file rule does not
 * apply. */
import type { SpinnerVariantId } from '@/stores/chatPrefsStore';
import puppyWebp from '@/assets/spinners/puppy.webp';
import puppyPoster from '@/assets/spinners/puppy-poster.png';
import girlWebp from '@/assets/spinners/girl-walk.webp';
import girlPoster from '@/assets/spinners/girl-poster.png';
import './spinnerThemes.css';

/**
 * True when the viewer asked for reduced motion. Read at render (a spinner is
 * short-lived, so we don't subscribe to changes) — the animated-WebP themes use
 * this to substitute a static poster frame, since CSS cannot pause a WebP.
 */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export interface SpinnerTheme {
  id: SpinnerVariantId;
  /** Human label for the settings selects. */
  name: string;
  /** Sized animation. `size` is the target icon height in px. */
  Component: (props: { size: number }) => JSX.Element;
  /** Rotating status phrases for this theme (≥8 each). */
  phrases: string[];
}

// ============ 1. COSMOS (orbit — the original indicator) ============
function CosmosSpinner({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 30 30"
      className="vsp-svg"
      aria-hidden="true"
    >
      <g className="vsp-orbit-ring">
        <circle cx="15" cy="15" r="10" fill="none" stroke="rgba(99,102,241,.3)" strokeWidth="1.2" />
        <circle cx="15" cy="5" r="2.4" fill="#818cf8" />
      </g>
    </svg>
  );
}

// ============ 2. ENCHANTMENT (magic wand + fairy dust) ============
// The wand art fills less of its 48-viewBox than the other icons fill theirs,
// so it renders at 1.5× the base size to match the operator-approved prototype
// proportion (the "wand not visible" fix) and stay legible next to the text.
function WandSpinner({ size }: { size: number }) {
  const px = Math.round(size * 1.5);
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 48 48"
      className="vsp-svg"
      aria-hidden="true"
    >
      <g className="vsp-wand-arm">
        <line x1="14" y1="35" x2="31" y2="16" stroke="#3b0f6e" strokeWidth="6" strokeLinecap="round" />
        <line x1="14" y1="35" x2="31" y2="16" stroke="url(#vsp-wandg)" strokeWidth="4" strokeLinecap="round" />
        <line x1="15.5" y1="33" x2="29" y2="18.5" stroke="#f5f3ff" strokeWidth="1" strokeLinecap="round" opacity=".65" />
        <g className="vsp-wand-tip">
          <path d="M33 5 L35.5 14 L44 16.5 L35.5 19 L33 28 L30.5 19 L22 16.5 L30.5 14 Z" fill="#fde68a" />
          <circle cx="33" cy="16.5" r="2.1" fill="#fffbeb" />
        </g>
      </g>
      <circle className="vsp-dust d1" cx="33" cy="17" r="1.6" fill="#f9a8d4" />
      <circle className="vsp-dust d2" cx="33" cy="17" r="1.3" fill="#93c5fd" />
      <circle className="vsp-dust d3" cx="33" cy="17" r="1.5" fill="#fde68a" />
      <circle className="vsp-dust d4" cx="33" cy="17" r="1.2" fill="#c4b5fd" />
      <defs>
        <linearGradient id="vsp-wandg" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#8b5cf6" />
          <stop offset="1" stopColor="#d8c7ff" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// ============ 3. DAY & NIGHT (spinning sun/moon coin + eclipse) ============
// The coin is a fixed 32px 3-D structure (craters/eclipse offsets are px-tuned);
// we scale it to `size` via a transform so callers get uniform sizing.
const DAYNIGHT_BASE = 32;
function DayNightSpinner({ size }: { size: number }) {
  const factor = size / DAYNIGHT_BASE;
  return (
    <div style={{ width: size, height: size, position: 'relative' }} aria-hidden="true">
      <div
        className="vsp-daynight"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) scale(${factor})`,
        }}
      >
        <div className="vsp-coin">
          <div className="vsp-coin-face vsp-coin-sun" />
          <div className="vsp-coin-face vsp-coin-moon">
            <span className="vsp-crater c1" />
            <span className="vsp-crater c2" />
            <span className="vsp-crater c3" />
          </div>
        </div>
        <div className="vsp-eclipse-corona" />
        <div className="vsp-eclipse-moon" />
      </div>
    </div>
  );
}

// ============ 4. PUPPY (i2v mini-movie: sit → run → jump, in place) ============
// Photo themes render larger than the SVG icons (matching the prototype's
// 48/60px photos vs ~28px icons) so the figure reads at spinner scale. The
// animated WebP carries its own motion, so the img gets no CSS transform; under
// reduced motion it falls back to the static first-frame poster.
function PuppySpinner({ size }: { size: number }) {
  return (
    <span className="vsp-photo-wrap" aria-hidden="true">
      <img
        src={prefersReducedMotion() ? puppyPoster : puppyWebp}
        alt=""
        loading="lazy"
        className="vsp-photo vsp-puppy"
        style={{ height: Math.round(size * 1.7), width: 'auto' }}
      />
    </span>
  );
}

// ============ 5. RAINY STROLL (i2v umbrella walk-cycle + falling rain) ============
const RAIN_OFFSETS = ['14%', '33%', '52%', '70%', '86%'];
const RAIN_DELAYS = ['0s', '.32s', '.16s', '.48s', '.62s'];
function RainySpinner({ size }: { size: number }) {
  return (
    <span className="vsp-photo-wrap" aria-hidden="true">
      <img
        src={prefersReducedMotion() ? girlPoster : girlWebp}
        alt=""
        loading="lazy"
        className="vsp-photo vsp-girl"
        style={{ height: Math.round(size * 2.1), width: 'auto' }}
      />
      {RAIN_OFFSETS.map((left, i) => (
        <span
          key={left}
          className="vsp-rd"
          style={{ left, animationDelay: RAIN_DELAYS[i] }}
        />
      ))}
    </span>
  );
}

export const SPINNER_THEMES: SpinnerTheme[] = [
  {
    id: 'cosmos',
    name: 'Cosmos',
    Component: CosmosSpinner,
    phrases: [
      'Consulting the aether',
      'Charting constellations',
      'Pondering the void',
      'Tracing starlight',
      'Weighing infinities',
      'Divining the pattern',
      'Communing with the cosmos',
      'Unfolding the manifold',
      'Listening to the spheres',
      'Threading the noosphere',
      'Contemplating being',
      'Gathering stardust',
      'Aligning the orbits',
      'Musing on eternity',
      'Wandering the nebulae',
      'Reading the firmament',
      // Restored from the pre-1.554.0 THINKING_PHRASES pool so the default
      // Cosmos theme keeps the full original phrase set (not a shrunk subset).
      'Questioning the real',
      'Sounding the deep',
      'Dreaming in vacuum',
      'Parsing the sublime',
    ],
  },
  {
    id: 'enchantment',
    name: 'Enchantment',
    Component: WandSpinner,
    phrases: [
      'Weaving a little magic',
      'Sprinkling fairy dust',
      'Conjuring an answer',
      'Whispering the incantation',
      'Stirring the cauldron',
      'Summoning sparkles',
      'Enchanting the words',
      'Casting the spell',
      'Brewing something clever',
      'Charming the pixels',
      'Coaxing the spirits',
      'Bottling the shimmer',
    ],
  },
  {
    id: 'daynight',
    name: 'Day & Night',
    Component: DayNightSpinner,
    phrases: [
      'Turning day into night',
      'Chasing the horizon',
      'Waiting for the dawn',
      "Tracing the sun's arc",
      'Rising with the moon',
      'Counting the hours',
      'Trading light for stars',
      'Eclipsing the doubt',
      'Orbiting the question',
      'Weathering the twilight',
      'Greeting the daybreak',
    ],
  },
  {
    id: 'puppy',
    name: 'Puppy',
    Component: PuppySpinner,
    phrases: [
      'Fetching your answer',
      'Chasing the thought',
      'Digging up the details',
      'Sniffing out the answer',
      'Wagging toward a reply',
      'Rolling it over',
      'Good dog, thinking hard',
      'Bounding after it',
      'Nosing through the notes',
      'Bringing it right back',
      'Pawing at the problem',
    ],
  },
  {
    id: 'rainy',
    name: 'Rainy Stroll',
    Component: RainySpinner,
    phrases: [
      'Strolling through the rain',
      'Walking it through',
      'Dancing between raindrops',
      'Wandering the puddles',
      'Opening the question',
      'Taking the scenic route',
      'Twirling the umbrella',
      'Splashing toward an answer',
      'Weathering the thought',
      'Braving the downpour',
      'Following the drizzle',
    ],
  },
];

/** All theme ids, in registry order — matches SPINNER_VARIANTS in the store. */
export const SPINNER_THEME_IDS: SpinnerVariantId[] = SPINNER_THEMES.map((t) => t.id);

/** Union of every theme's phrases, for the 'rotate' message pool. */
export const ALL_SPINNER_PHRASES: string[] = SPINNER_THEMES.flatMap((t) => t.phrases);

/** Look up a theme by id, falling back to cosmos (index 0) for unknown ids. */
export function resolveSpinnerTheme(id: string): SpinnerTheme {
  return SPINNER_THEMES.find((t) => t.id === id) ?? SPINNER_THEMES[0];
}
